import { Dropdown } from "@nova/component-dropdown"
import * as Icons from "@nova/component-icons"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { Comment } from "@nova/schemas"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectUserID } from "user/profile/selectors"
import { setGlobalNotificationContext } from "user/profile/slice"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { selectActivatedFireId } from "features/map/control/selectors"
import { addEntitySocket, deleteEntitySocket } from "features/entity-caching/project/slice"
import { selectMapCommentById } from "features/entity-caching/map-comments/selectors"
import { useDeleteComment } from "features/comments/Comments"
import { ENTITY_ID_PARAM } from "features/map/useEntityIdDeepLink"
import { useRouter } from "pages"
import { Divider } from "components/Divider"

export const CommentMoreMenu = ({
    comment,
    mapEntityId,
    onDelete,
}: {
    comment: Comment<"map-pin" | "map-comment">
    mapEntityId: string
    onDelete?: () => void
}) => {
    const dispatch = useAppDispatch()
    const meUserId = useAppSelector(selectUserID) ?? ""
    const workspaceId = useAppSelector(selectActiveWorkspaceId())
    const projectId = useAppSelector(selectActivatedFireId)
    const { deleteComment } = useDeleteComment()
    const router = useRouter("/")
    const pin = useAppSelector(selectMapCommentById(mapEntityId))
    const isMine = comment.createdBy === meUserId

    const removeComment = async () => {
        onDelete?.()
        const isRootPin = comment.context.target === "map-pin"
        if (isRootPin) {
            dispatch(deleteEntitySocket({ path: ["mapComments", { id: mapEntityId }] }))
        }
        try {
            await deleteComment({ commentId: comment.id })
        } catch (error) {
            if (isRootPin && pin) {
                dispatch(addEntitySocket({ data: pin, path: ["mapComments"] }))
            }
            throw error
        }
    }

    const copyLink = async () => {
        const path =
            workspaceId && projectId
                ? router.preview({
                      path: "/workspace/:workspaceId/project/:projectId",
                      params: { workspaceId, projectId },
                      search: { [ENTITY_ID_PARAM]: mapEntityId },
                  })
                : undefined
        const link = path && new URL(path, window.location.origin).href
        try {
            if (!link) throw new Error("Missing project context for comment link")
            await navigator.clipboard.writeText(link)
            dispatch(
                setGlobalNotificationContext({
                    message: "Copied link to clipboard.",
                    status: GlobalNotificationStatus.success,
                    timeoutInMs: 5000,
                })
            )
        } catch {
            dispatch(
                setGlobalNotificationContext({
                    message: "Failed to copy link to clipboard. Try again, staying on this tab.",
                    status: GlobalNotificationStatus.error,
                    timeoutInMs: 5000,
                })
            )
        }
    }

    return (
        <Dropdown>
            <Dropdown.Trigger onClick={(e) => e.stopPropagation()}>
                <Icons.SmallIcon
                    Icon={Icons.MoreVert}
                    className="text-gray-300 hover:text-gray-0 cursor-pointer"
                />
            </Dropdown.Trigger>
            <Dropdown.Menu
                position="bottom-right"
                className="min-w-[160px]"
                onClick={(e) => e.stopPropagation()}
            >
                <Dropdown.ButtonItem onClick={copyLink}>
                    <div className="flex items-center gap-3">
                        <Icons.SmallIcon Icon={Icons.Link} />
                        Copy link
                    </div>
                </Dropdown.ButtonItem>
                <Divider className="!bg-white-100" />
                {isMine && (
                    <Dropdown.ButtonItem onClick={removeComment}>
                        <div className="flex items-center gap-3 text-red-500">
                            <Icons.SmallIcon Icon={Icons.Trash} />
                            <span>Delete comment</span>
                        </div>
                    </Dropdown.ButtonItem>
                )}
            </Dropdown.Menu>
        </Dropdown>
    )
}
